import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';

const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const investmentFocus = [
    {
      title: 'Multifamily Properties',
      description: 'Class B and C apartment communities across Greater Houston with value-add potential through renovations, operational improvements, and market repositioning.',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/pgbhebdp_666f25fc-b975-432d-a6cd-45b38c09faa0.png',
      icon: (
        <svg className="w-5 h-5 text-[#0A1628]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      title: 'Retail Centers',
      description: 'Neighborhood and community retail centers in Houston metro requiring lease-up, tenant mix optimization, and capital improvements.',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/n5uhqmr2_69fdc939-5875-4eba-9889-e6fa379f0577.png',
      icon: (
        <svg className="w-5 h-5 text-[#0A1628]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: 'Distressed Assets',
      description: 'Opportunistic acquisitions of distressed properties in Texas requiring turnaround expertise and repositioning strategies.',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/l2cpduz0_1a31294a-313a-4573-ab32-15dd18f31129.png',
      icon: (
        <svg className="w-5 h-5 text-[#0A1628]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: 'Private Lending Solutions',
      description: 'Access to private lenders with attractive rates when conventional financing isn\'t available for real estate investments.',
      image: 'https://customer-assets.emergentagent.com/job_tx-property-experts/artifacts/fr3osnak_05abadb8-1274-4721-84ed-40bab5cf039e.png',
      icon: (
        <svg className="w-5 h-5 text-[#0A1628]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  return (
    <section id="about" className="relative gradient-section py-8 lg:py-12 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-[#D4AF37]/5 rounded-full blur-[80px]" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#1a2940]/50 rounded-full blur-[60px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 xl:px-16">
        {/* Section Header - Compact */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-6 lg:mb-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full border border-[#D4AF37]/30 mb-3"
            variants={itemVariants}
            whileHover={{ scale: 1.05, borderColor: 'rgba(212, 175, 55, 0.6)' }}
          >
            <span className="text-label text-[#D4AF37]">Investment Focus</span>
          </motion.div>
          <motion.h2 
            className="heading-h2 mb-2"
            variants={itemVariants}
          >
            Disciplined Approach to
            <span className="gradient-text"> Value Creation</span>
          </motion.h2>
          <motion.p 
            className="text-body-sm"
            variants={itemVariants}
          >
            Asset classes where our operational expertise and Greater Houston market knowledge create sustainable competitive advantages for accredited investors.
          </motion.p>
        </motion.div>

        {/* Investment Focus Cards - Premium Dark Style, Taller & Narrower */}
        <motion.div 
          className="grid md:grid-cols-2 gap-4 lg:gap-5 mb-8 max-w-5xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-30px" }}
          variants={containerVariants}
        >
          {investmentFocus.map((item, index) => (
            <motion.div 
              key={index} 
              variants={cardVariants}
              className="h-full"
            >
              <Card className="group card-modern rounded-xl overflow-hidden h-full flex flex-col bg-gradient-to-br from-[#111D35] to-[#0A1628] hover:shadow-xl hover:shadow-[#D4AF37]/10 transition-all duration-500">
                {/* Taller Image Area with proper framing */}
                <div className="relative h-52 lg:h-64 overflow-hidden">
                  <motion.img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                  {/* Dark gradient overlay for consistency */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/30 to-transparent opacity-70" />
                </div>
                
                {/* Content Area with Dark Blue Background */}
                <div className="p-5 lg:p-6 flex-1 flex flex-col bg-gradient-to-b from-[#111D35]/95 to-[#0A1628]">
                  <div className="flex items-center gap-3 mb-3">
                    {/* Animated Gold Icon */}
                    <motion.div 
                      className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/30"
                      whileHover={{ scale: 1.15, rotate: 5 }}
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      {item.icon}
                    </motion.div>
                    <h3 className="text-white text-base lg:text-lg font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed flex-1">
                    {item.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Vertical Integration Section - Compact */}
        <motion.div 
          className="glass-strong rounded-xl p-5 lg:p-6 border border-white/10 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div 
                className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full mb-4"
                whileHover={{ scale: 1.05 }}
              >
                <span className="text-label text-[#0A1628]">Vertically Integrated</span>
              </motion.div>
              
              <h3 className="heading-h3 mb-3">
                Complete Control Over <span className="gradient-text">Asset Performance</span>
              </h3>
              
              <p className="text-body-sm mb-5">
                As a vertically integrated real estate firm, our in-house capabilities enable complete control over asset performance, cost efficiency, and value creation across Texas.
              </p>
              
              {/* Vertical Integration Items - Inline */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { title: 'Property Mgmt', icon: '🏢' },
                  { title: 'Insurance', icon: '🛡️' },
                  { title: 'Data-Driven', icon: '📊' }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="card-modern rounded-lg p-3 text-center"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <span className="text-lg block mb-1">{item.icon}</span>
                    <span className="text-white/70 text-xs font-medium">{item.title}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Right - Leadership Credentials */}
            <motion.div 
              className="card-modern border border-[#D4AF37]/20 rounded-xl p-4 h-full flex flex-col"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ boxShadow: '0 0 25px rgba(212, 175, 55, 0.1)' }}
            >
              <h4 className="heading-h4 text-center mb-4">
                Leadership Credentials
              </h4>
              
              <div className="space-y-2 flex-1 flex flex-col justify-center">
                {[
                  { icon: '🎓', text: 'M.S. Industrial Engineering & Management' },
                  { icon: '📍', text: 'Licensed Real Estate Agent since 2005' },
                  { icon: '⏱️', text: '18+ Years Houston Market Experience' }
                ].map((credential, index) => (
                  <motion.div 
                    key={index}
                    className="bg-[#0f1a2a] border border-white/5 rounded-lg p-2.5 flex items-center gap-2.5"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                    whileHover={{ scale: 1.01, borderColor: 'rgba(212, 175, 55, 0.2)' }}
                  >
                    <div className="w-7 h-7 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm">{credential.icon}</span>
                    </div>
                    <span className="text-white/80 text-xs">{credential.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
